import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LayoutDashboard, CreditCard, LogOut, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/browse', label: 'Browse Friends' },
  { to: '/fwb', label: 'FWB (18+)' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const isPremium = userProfile?.subscriptionStatus === 'premium' || userProfile?.subscriptionStatus === 'active';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    setOpen(false);
    signOut();
    navigate('/');
  };

  const linkClass = 'text-white/90 hover:text-brand-pink font-medium transition-colors';
  const mobileLinkClass = 'px-4 py-2 text-white/90 hover:bg-white/10 rounded-lg flex items-center gap-2';

  return (
    <header className="sticky top-0 z-50 backdrop-blur bg-slate-900/95 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center shrink-0 py-1" aria-label="Stranger69.net Home">
            <img src="/st69_logo_new.png" alt="Stranger69.net" className="h-11 sm:h-12 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass}>
                {label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/90 hover:bg-white/10 transition-colors"
                  aria-expanded={profileOpen}
                  aria-haspopup="true"
                >
                  <span className="w-8 h-8 rounded-full bg-brand-pink/30 flex items-center justify-center">
                    <User className="h-4 w-4 text-brand-pink" />
                  </span>
                  <span className="font-medium max-w-[120px] truncate">
                    {userProfile?.displayName || user.email?.split('@')[0] || 'Account'}
                  </span>
                  {isPremium && <Crown className="h-4 w-4 text-amber-400" />}
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-1 w-56 py-2 rounded-xl bg-slate-800 border border-white/10 shadow-xl">
                    <div className="px-4 py-2 border-b border-white/10">
                      <p className="text-sm font-medium text-white truncate">{user.email}</p>
                      <p className="text-xs text-slate-400">
                        {isPremium ? 'Premium' : 'Free'}
                      </p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-4 py-2 text-white/90 hover:bg-white/10"
                      onClick={() => setProfileOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                    <Link
                      to="/payment"
                      className="flex items-center gap-2 px-4 py-2 text-white/90 hover:bg-white/10"
                      onClick={() => setProfileOpen(false)}
                    >
                      <CreditCard className="h-4 w-4" />
                      {isPremium ? 'Subscription' : 'Upgrade'}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2 text-white/90 hover:bg-white/10"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className={linkClass}>
                  Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark transition-colors"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/10"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col gap-2">
              {navLinks.map(({ to, label }) => (
                <Link key={to} to={to} className={mobileLinkClass} onClick={() => setOpen(false)}>
                  {label}
                </Link>
              ))}
              {user ? (
                <>
                  <div className="px-4 py-2 mt-2 border-t border-white/10">
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    <p className="text-xs text-slate-500">{isPremium ? 'Premium' : 'Free'}</p>
                  </div>
                  <Link to="/dashboard" className={mobileLinkClass} onClick={() => setOpen(false)}>
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link to="/payment" className={mobileLinkClass} onClick={() => setOpen(false)}>
                    <CreditCard className="h-4 w-4" />
                    {isPremium ? 'Subscription' : 'Upgrade'}
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={mobileLinkClass + ' w-full text-left'}
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2 mt-4 px-4">
                  <Link
                    to="/login"
                    className="flex-1 py-2 text-center rounded-xl border border-white/30 text-white font-medium"
                    onClick={() => setOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex-1 py-2 text-center rounded-xl bg-brand-pink text-white font-semibold"
                    onClick={() => setOpen(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
